import { Loading } from '@/components'
import { useTracking } from '@/components/Functional/hooks/useTracking'
import styles from '@/styles/OcrFrame.module.css'
import { IconQuestionMark, IconX } from '@tabler/icons-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCameraStream, getMobileOS, useInitCameraStream } from './helpers'
export { getStaticPaths, getStaticProps } from '@/components/Functional/utils/getStaticFunctions'
export { Warnings } from './warnings'

type PhotoFrameProps = {
  /** シャッター押下後に撮影画像の DataURL を受け取るコールバック */
  onCaptured: (imageDataUrl: string) => void
  /** 「読み取り方」を押下した時の処理 */
  onGoToWarning: () => void
  onClose?: () => void
}

export const OcrFrame = ({ onClose, onCaptured, onGoToWarning }: PhotoFrameProps) => {
  const canvasDimensions = { height: 1920, width: 1080 }
  const { t } = useTranslation()

  const videoCanvasRef = useRef<HTMLCanvasElement>(null)
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | undefined>(undefined)
  // `dataRef.current`にデータが入っているので、これをPOSTのデータとして利用する
  const dataRef = useRef<string | undefined>(undefined)
  const audioStreamRef = useRef<MediaStream | undefined>(undefined)

  // パフォーマンス最適化用のref
  const isRecordingRef = useRef<boolean>(false)
  const previewDrawFrameId = useRef<number | undefined>(undefined)

  const [record, setRecord] = useState(false)
  const [loading, setLoading] = useState(true)
  const [videoRecordingError, setVideoRecordingError] = useState<string>('')
  const { tracking, getLocation } = useTracking({ page: 'photo_frame' })

  const frontCameraUse = false
  const videoRef = useInitCameraStream(frontCameraUse)
  const mobileOS = getMobileOS()

  const frameStyle = () => {
    let className = styles.frame
    if (mobileOS === 'iOS') {
      className += ` ${styles.frame_ios}`
    } else if (mobileOS === 'Android') {
      className += ` android`
    }
    return className
  }

  //ウェッブカメラのstreamをcanvasに表示
  const playPreview = useCallback(async () => {
    // const frames = await preloadImages(photoFrame?.map((s) => s.file))

    const [ch, cw] = [canvasDimensions.height, canvasDimensions.width]
    const videoCanvas = videoCanvasRef.current

    const offscreenCanvas = document.createElement('canvas')
    offscreenCanvas.height = ch
    offscreenCanvas.width = cw
    offscreenCanvasRef.current = offscreenCanvas

    const offScreenCtx = offscreenCanvas?.getContext('2d')
    const videoCtx = videoCanvas?.getContext('2d')

    const video = videoRef.current

    if (!(offScreenCtx && videoCtx && video)) return

    video?.addEventListener('canplay', () => {
      setLoading(false)
    })

    const canvasContainer = document.getElementById('canvas-container')
    if (!canvasContainer) return

    const drawFrame = () => {
      // 録画中はプレビュー描画を停止
      if (isRecordingRef.current) {
        return
      }

      videoCtx.drawImage(video, 0, 0, cw, ch)

      if (!videoCanvas) {
        return
      }
      offScreenCtx.drawImage(videoCanvas, 0, 0)
      const canvases = document.getElementsByClassName(`${styles.image_canvas}`)
      for (const canvas of Array.from(canvases)) {
        if (canvas instanceof HTMLCanvasElement) {
          offScreenCtx.drawImage(canvas, 0, 0)
        }
      }

      previewDrawFrameId.current = requestAnimationFrame(drawFrame)
    }

    video.addEventListener('loadedmetadata', () => {
      // 既存の RAF ループがあればキャンセルしてから新ループを開始（バックグラウンド復帰時の重複防止）
      if (previewDrawFrameId.current) {
        cancelAnimationFrame(previewDrawFrameId.current)
      }
      previewDrawFrameId.current = requestAnimationFrame(drawFrame)
    })
    video.load()
  }, [])

  useEffect(() => {
    playPreview()
  }, [])

  useEffect(() => {
    if (videoRecordingError) {
      getLocation().then((v) => tracking({ page: 'photo_frame', optionalData: videoRecordingError, ...v }))
      setVideoRecordingError('')
    }
  }, [videoRecordingError])

  useEffect(() => {
    const resumeVideoPlayback = () => {
      const video = videoRef.current

      if (!video) {
        return
      }

      // フォアグラウンドに戻ったら動画を再生する
      if (document.visibilityState === 'visible') {
        getCameraStream(frontCameraUse).then((stream) => {
          video.srcObject = stream ?? null
          video.play()
        })
        handleScreenReturn()
      } else {
        const stream = video.srcObject as MediaStream
        if (stream) {
          for (const track of stream.getTracks()) {
            track.stop()
          }
        }

        const audioStream = audioStreamRef.current
        if (audioStream) {
          for (const track of audioStream.getTracks()) {
            track.stop()
          }
          audioStreamRef.current = undefined
        }

        if (video.srcObject) {
          video.pause()
          video.srcObject = null
        }
      }
    }

    // ページ離脱後にページに再アクセスされた際に、映像が停止しないように修正する.
    document.addEventListener('visibilitychange', resumeVideoPlayback)

    return () => {
      document.removeEventListener('visibilitychange', resumeVideoPlayback)
    }
  }, [])

  const handleScreenReturn = () => {
    const mediaRecorder = mediaRecorderRef.current

    if (!mediaRecorder) return
    if (mediaRecorder.state !== 'recording') return

    // 録画フラグを停止
    isRecordingRef.current = false

    //処理
    mediaRecorder.stop()
    mediaRecorderRef.current = undefined
    setRecord(false)
  }

  //画像保存
  const handleImageCapture = () => {
    const videoCanvas = videoCanvasRef.current
    if (!videoCanvas) return

    const ctx = videoCanvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(videoCanvas, 0, 0)

    const canvases = document.getElementsByClassName(`${styles.image_canvas}`)
    for (const canvas of Array.from(canvases)) {
      if (canvas instanceof HTMLCanvasElement) {
        const rect = canvas.getBoundingClientRect()
        const videoCanvasRect = videoCanvas.getBoundingClientRect()
        // 推測だ以前は画面のサイズ固定の画像だけだったが、動かせるようになった際の考慮ができていなかった。
        // そのため、canvasのフレームサイズ限界まで広げようとしていたが、動かせる画像はフレームいっぱいではないので、縦方向のサイズの調整が必要になっている。
        // canvasの実際のサイズに変換する
        // 描画のrectは小さいのが、canvasは1080x1920とかなので大きくする
        const canvasRatio = {
          width: videoCanvas.width / videoCanvasRect.width,
          height: videoCanvas.height / videoCanvasRect.height,
        }

        const x = (rect.left - videoCanvasRect.left) * canvasRatio.width
        const y = (rect.top - videoCanvasRect.top) * canvasRatio.height
        const width = rect.width * canvasRatio.width
        const height = rect.height * canvasRatio.height

        ctx.drawImage(canvas, x, y, width, height)
      }
    }
    const dataUrl = videoCanvas.toDataURL('image/jpeg', 1.0)
    dataRef.current = dataUrl
  }

  // 撮影によりアンマウントされる場合のフラグ。撮影後はプレビュー（OcrPreview）へ遷移し、
  // OcrPreview が同じダミー history エントリを引き継いでバックを制御するため、ここでは back() しない。
  const hasCapturedRef = useRef(false)

  //シャッターをおす関数
  const onShutterClick = () => {
    handleImageCapture()
    if (!dataRef.current) {
      return
    }
    hasCapturedRef.current = true
    onCaptured(dataRef.current)
  }

  // 常に最新の onClose を参照するための ref（再レンダリングで参照が変わっても安全）
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // onClose の多重呼び出しを防ぐフラグ
  const hasClosedRef = useRef(false)

  // ブラウザバックを押した際に idle へ遷移させるため、マーカー付きのダミー history エントリを積む。
  // 依存配列を [] にすることでマウント時に1回だけ実行される。
  useEffect(() => {
    // 既にマーカーがある場合（プレビューからの撮り直しで再マウントした等）は多重に積まない。
    // これを積み直すとダミー履歴が累積し、ブラウザバックが複数回分必要になる不具合につながる。
    if (!window.history.state?.ocrOverlay) {
      window.history.pushState({ ocrOverlay: true }, '', window.location.href)
    }

    const handlePopState = () => {
      if (hasClosedRef.current) return
      hasClosedRef.current = true
      onCloseRef.current?.()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      // 閉じる操作なしにアンマウントされた場合、積んだダミーエントリを戻して履歴スタックの汚れを防ぐ。
      // ただし撮影によるアンマウント時は OcrPreview がこのエントリを引き継ぐため back() しない
      // （ここで back() すると OcrPreview の popstate を誤発火させてしまう）。
      // また、マーカーが残っている場合のみ back し、通常の履歴まで戻さないようガードする。
      if (!hasClosedRef.current && !hasCapturedRef.current && window.history.state?.ocrOverlay) {
        hasClosedRef.current = true
        window.history.back()
      }
    }
  }, [])

  // 閉じるボタン押下時: history を戻しつつ onClose を直接呼ぶ（popstate の非同期到達に依存しない）
  const handleClose = () => {
    if (hasClosedRef.current) return
    hasClosedRef.current = true
    // マーカーが残っている場合のみ back し、通常の履歴まで戻さないようガードする。
    if (window.history.state?.ocrOverlay) {
      window.history.back()
    }
    onCloseRef.current?.()
  }

  useEffect(() => {
    const cbk = () => {
      const frame = document.getElementById('ar_frame')
      if (!frame) return
      const { height: frameHeight } = frame.getBoundingClientRect()
      frame.style.setProperty('--frame-height', `${frameHeight.toFixed(2)}px`)
    }
    cbk()
    window.addEventListener('resize', cbk)

    return () => {
      window.removeEventListener('resize', cbk)
    }
  }, [])

  useEffect(() => {
    return () => {
      const audio = audioStreamRef.current

      if (audio) {
        for (const t of audio.getTracks()) {
          t.stop()
        }
      }

      // プレビュー描画を停止
      if (previewDrawFrameId.current) {
        cancelAnimationFrame(previewDrawFrameId.current)
      }

      // 録画フラグをリセット
      isRecordingRef.current = false
    }
  }, [])

  return (
    <div className={`${styles['frame-container']}`}>
      {loading && <Loading />}
      <div className={frameStyle()} id="ar_frame">
        <canvas
          className={styles.video_canvas}
          ref={videoCanvasRef}
          height={canvasDimensions.height}
          width={canvasDimensions.width}
        />
        <div id="canvas-container" className={styles['canvas-container']}>
          {/* このdivタグでカメラを描画している */}
        </div>
        <button
          type="button"
          className={styles.close_button}
          onClick={handleClose}
          aria-label={t('ocr.close', '閉じる')}
        >
          <IconX color="#fff" size={24} />
        </button>
        <div className={styles['guide-lines']}>
          <div className={styles['guide-line']}></div>
          <div className={styles['guide-line']}></div>
        </div>
        <video className={styles.video} src="" autoPlay ref={videoRef} muted playsInline />
      </div>

      <div className={`${styles.controls}`}>
        <div className="text-center"></div>

        <div className={styles['shutter-container']}>
          <button type="button" className={`${styles.shutter} ${record && styles.active}`} onClick={onShutterClick} />
        </div>

        <div className={'flex gap-4'}>
          <button className={styles.reset} onClick={onGoToWarning}>
            <IconQuestionMark color="#fff" size={24} />
            <p className="text-[#fff] text-[12px] whitespace-nowrap mt-[5px]">{t('ocr.how_to_read', '読み取り方')}</p>
          </button>
        </div>
      </div>

      {/*<ConfirmationModal*/}
      {/*  show={showConfirmationModal}*/}
      {/*  dataSrc={dataRef.current}*/}
      {/*  mediaType={'image'}*/}
      {/*  onClose={onModalClose}*/}
      {/*  onSave={onSave}*/}
      {/*/>*/}
    </div>
  )
}
